import { domQuerySelector } from 'pf-frontend-common/src/helper/dom/query-selector';
import {
  CtaFilterPropertyType,
  CtaSortContactedPropertyType,
  CtaSortMyNotesPropertyType,
  CtaSortSavedPropertyType,
} from 'pf-frontend-common/src/module/stats/types';
import { ViewBasicInterface } from 'pf-frontend-common/src/module/view/basic.interface';
import { StatsContexterServiceInterface } from 'pf-frontend-common/src/service/stats-contexter/service.interface';

import { BackendConfigAbTestsInterface } from 'common/data/backend/config/ab-tests.interface';
import { PropertyModel } from 'common/data/property/model';
import { PropertyStoreSearchOutputInterface } from 'common/data/property/store/search/output.interface';
import { PropertyContactedModel } from 'common/data/property-contacted/model';
import { PropertyContactedStoreEvent } from 'common/data/property-contacted/store.event';
import { PropertySavedModel } from 'common/data/property-saved/model';
import { PropertySavedStoreEvent } from 'common/data/property-saved/store.event';
import { SaveSearchStore } from 'common/data/save-search/store';
import { SaveSearchStoreEvent } from 'common/data/save-search/store.event';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { PreactMarkupComponentsInterface } from 'common/helper/preact/markup/components.interface';
import { EmailAlertUnsubscribeView } from 'common/module/email-alert/unsubscribe/view';
import { MyAccountFilterOptionsInterface } from 'common/module/my-account/filter-options.interface';
import { MyAccountHtmlElementInterface } from 'common/module/my-account/html-element.interface';
import { MyAccountListTypeEnum } from 'common/module/my-account/list-type.enum';
import { MyAccountMenuViewInterface } from 'common/module/my-account/menu/view.interface';
import { MyAccountPopupView } from 'common/module/my-account/popup/view';
import { MyAccountSortOptionEnum } from 'common/module/my-account/sort-option.enum';
import { MyAccountStatechartEvent } from 'common/module/my-account/statechart.event';
import { MyAccountStatechartActionEnum } from 'common/module/my-account/statechart/action.enum';
import { MyAccountStore } from 'common/module/my-account/store';
import { MyAccountViewStrategyInterface } from 'common/module/my-account/view/strategy.interface';
import { MyAccountViewCssSelectorEnum } from 'common/module/my-account/view-css-selector.enum';
import { MyAccountViewOptionsInterface } from 'common/module/my-account/view-options.interface';
import { PromiseCancelable } from 'common/module/promise/cancelable';
import { PromiseCancelableInterface } from 'common/module/promise/cancelable.interface';
import { PropertyListViewStore } from 'common/module/property/list/view-store';
import { PropertyListViewStoreEvent } from 'common/module/property/list/view-store.event';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { StatsTealiumAdapter } from 'common/module/stats/tealium/adapter';
import { StatsTealiumDataLayerEventInterface } from 'common/module/stats/tealium/data-layer/event.interface';
import { StatsTealiumDataLayerEventActionEnum } from 'common/module/stats/tealium/data-layer/event-action.enum';
import { StatsTealiumDataLayerEventCategoryEnum } from 'common/module/stats/tealium/data-layer/event-category.enum';
import { StatsTealiumDataLayerTealiumEventEnum } from 'common/module/stats/tealium/data-layer/tealium-event.enum';
import { StatsTealiumProviderInterface } from 'common/module/stats/tealium/provider.interface';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';
import { MyNotesServiceInterface } from 'common/service/my-notes/service.interface';
import { PropertyContactedStoreServiceInterface } from 'common/service/property-contacted-store/service.interface';
import { PropertySavedStoreServiceInterface } from 'common/service/property-saved-store/service.interface';
import { PropertyStoreServiceInterface } from 'common/service/property-store/service.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';
import { preactMarkup } from 'common/typings/preact-markup';

export class MyAccountView implements ViewBasicInterface {
  /**
   * Main HTML element
   */
  private element: HTMLElement;

  /**
   * Html elements
   */
  private htmlElement: MyAccountHtmlElementInterface;

  /**
   * Cancelable fetch properties promise
   */
  private fetchPropertiesCancelable: PromiseCancelableInterface<PropertyStoreSearchOutputInterface>;

  /**
   * Strategy used to specialize some methods for filters and sorting
   */
  private strategy: MyAccountViewStrategyInterface;

  /**
   * List type
   */
  private listType: MyAccountListTypeEnum;

  /**
   * Constructor
   */
  constructor(
    private propertyListViewDynamic: ViewDynamicInterface<PropertyListViewStore>,
    private propertySavedStoreService: PropertySavedStoreServiceInterface,
    private propertyContactedStoreService: PropertyContactedStoreServiceInterface,
    private myAccountStatechartStore: StatechartStoreInterface,
    private statsService: StatsServiceInterface,
    private myAccountPopupView: MyAccountPopupView,
    private myAccountMenuView: MyAccountMenuViewInterface,
    private statsContexterService: StatsContexterServiceInterface,
    private propertyStoreService: PropertyStoreServiceInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private myAccountStore: MyAccountStore,
    private myNoteService: MyNotesServiceInterface,
    private saveSearchStoreService: SaveSearchStore,
    private abTests: BackendConfigAbTestsInterface,
    private myNotesGetIsAvailable: boolean,
    private preactMarkupHelper: preactMarkup,
    private components: PreactMarkupComponentsInterface,
    private emailAlertUnsubscribeView: EmailAlertUnsubscribeView,
    private statsTealium: StatsTealiumProviderInterface
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: MyAccountViewOptionsInterface): void {
    // Keep HTML element
    this.element = options.element;

    this.listType = options.listType;

    this.htmlElement = {
      menu: domQuerySelector(this.element, MyAccountViewCssSelectorEnum.menu),
      propertyList: domQuerySelector(this.element, MyAccountViewCssSelectorEnum.propertyList),
      saveSearchListComponent: domQuerySelector(this.element, MyAccountViewCssSelectorEnum.savedSearchList),
    };

    this.preactMarkupHelper(this.element, this.components);

    // Init my account store
    this.myAccountStore.initialize();

    // Initialize my account menu
    this.initMyAccountMenu(options.isUserInfoHidden);

    this.strategy.initialize(options);
    // Initialize subviews
    this.initPropertyList(options.showDeleteButton);

    this.myAccountPopupView.initialize({
      title: i18nTranslate(
        this.isPageTypeSavedSearches
          ? 'save_search:deleteAll:confirmation:title'
          : 'property:deleteAll:confirmation:title'
      ),
      description: i18nTranslate(
        this.isPageTypeSavedSearches
          ? 'save_search:deleteAll:confirmation:description'
          : 'Are you sure you want to delete all properties?'
      ),
    });

    // Initialize stats
    this.initStats();

    this.observers();

    this.transitEventFetchProperties();

    this.initEmailAlertUnsubscribe();
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    // Log stats
    this.stats();

    // Render properties
    this.renderPropertyList();

    this.strategy.render();

    this.renderEmailAlertUnsubscribe();
  }

  /**
   * Sets the strategy
   */
  public setStrategy(strategy: MyAccountViewStrategyInterface): void {
    this.strategy = strategy;
  }

  /**
   * Transit saved/contacted/notes properties event
   */
  private transitEventFetchProperties(): void {
    if (this.myNotesGetIsAvailable) {
      this.myAccountStatechartStore.transit({
        event: MyAccountStatechartEvent.fetchNotesProperties,
        payload: {},
      });
    }

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.fetchContactedProperties,
      payload: {},
    });

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.fetchSavedProperties,
      payload: {},
    });
  }

  /**
   * Initialize stats
   */
  private initStats(): void {
    this.statsContexterService.setAbTests(this.abTests);

    if (this.listType === MyAccountListTypeEnum.contacted) {
      this.statsContexterService.setMyAccountContactedPage(true);
    } else if (this.listType === MyAccountListTypeEnum.notes) {
      this.statsContexterService.setMyAccountMyNotesPage(true);
    } else if (this.listType === MyAccountListTypeEnum.saved) {
      this.statsContexterService.setMyAccountSavedPage(true);
    }
  }

  /**
   * Init my account menu
   */
  private initMyAccountMenu(isUserInfoHidden: boolean): void {
    if (!this.htmlElement.menu) {
      return;
    }

    this.myAccountMenuView.initialize({
      element: this.htmlElement.menu,
      isUserInfoHidden,
    });
  }

  /**
   * Init email alert unsubscribe
   */
  private initEmailAlertUnsubscribe(): void {
    this.emailAlertUnsubscribeView.initialize();
  }

  /**
   * Log stats for the page
   */
  private stats(): void {
    // Log stats
    this.statsService.pageView();
    if (this.listType) {
      this.statsService.myAccountPage();
    }
  }

  /**
   * returns true if active page is saved-searches
   */
  private get isPageTypeSavedSearches(): boolean {
    return this.listType === MyAccountListTypeEnum.searches;
  }

  /**
   * @inheritDoc
   */
  private observers(): void {
    if (this.listType === MyAccountListTypeEnum.contacted) {
      this.propertyContactedStoreService
        .getEventEmitter()
        .addListener(PropertyContactedStoreEvent.firstLoading, this.onSyncPropertyContactedStore);

      this.propertyContactedStoreService
        .getEventEmitter()
        .addListener(PropertyContactedStoreEvent.clear, this.onClearPropertyStore);
    } else if (this.listType === MyAccountListTypeEnum.saved) {
      this.propertySavedStoreService
        .getEventEmitter()
        .addListener(PropertySavedStoreEvent.firstLoading, this.onSyncPropertySavedStore);

      this.propertySavedStoreService
        .getEventEmitter()
        .addListener(PropertySavedStoreEvent.clear, this.onClearPropertyStore);
    } else if (this.isPageTypeSavedSearches) {
      this.userAuthenticationService
        .getEventEmitter()
        .addListener(UserAuthenticationEvent.signInSucceeded, this.fetchSaveSearches);
    }

    this.strategy.observers();

    // Log Out Succeeded
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.logOutSucceeded, this.onLogOutSucceeded);

    // Fetching saved properties
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.fetchSavedProperties, this.onFetchSavedPropertiesAction);

    // Fetching contacted properties
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.fetchContactedProperties, this.onFetchContactedPropertiesAction);

    // Start filtering properties
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.filter, this.onFilterAction);

    // Start sorting properties
    this.myAccountStatechartStore.getEventEmitter().addListener(MyAccountStatechartActionEnum.sort, this.onSortAction);

    // Update properties list
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.updateListProperties, this.onUpdateListPropertiesAction);

    // Cancel fetching properties
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.cancelPreviousFetch, this.onCancelPreviousFetchAction);

    // Enable properties list
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.enableList, this.onEnableListAction);

    // Disable properties list
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.disableList, this.onDisableListAction);

    // Delete all properties from view
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.deleteAllViewData, this.onDeleteAllViewDataAction);

    // Delete all properties from local storage
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.deleteAllIndexDBData, this.onDeleteAllLocalStorageDataAction);

    // Delete all properties from API
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.deleteAllApiData, this.onDeleteAllApiDataAction);

    // Delete one property from view
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.deleteViewData, this.onDeleteViewDataAction);

    // Clear all properties from view
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.clearAll, this.onClearAllAction);

    // Delete one property from local storage
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.deleteIndexDBData, this.onDeleteLocalStorageDataAction);

    // Delete one property from Api
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.deleteApiData, this.onDeleteApiDataAction);

    // Show confirmation popup before deleting property
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.showConfirmationPopup, this.onShowConfirmationPopup);

    // Hide confirmation popup after deleting property
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.hideConfirmationPopup, this.onHideConfirmationPopup);

    // Check if we need to refresh properties list
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.checkIfRefreshListIsNeeded, this.onCheckIfRefreshListIsNeeded);

    // Delete one note from view
    this.myAccountStatechartStore
      .getEventEmitter()
      .addListener(MyAccountStatechartActionEnum.deleteNoteViewData, this.onDeleteNoteViewData);
  }

  /**
   * Init my account list
   */
  private initPropertyList(showDeleteButton: boolean): void {
    if (!this.htmlElement.propertyList) {
      return;
    }

    if (!this.isPageTypeSavedSearches) {
      this.propertyListViewDynamic.initialize({
        element: this.htmlElement.propertyList,
      });

      this.propertyListViewDynamic.getViewStore().initialize({
        showDeleteButton,
        isTargetBlank: false,
        listType: this.listType,
      });

      // Delete one property from view
      this.propertyListViewDynamic
        .getViewStore()
        .getEventEmitter()
        .addListener(PropertyListViewStoreEvent.deleteProperty, this.onDeletePropertyPropertyList);

      // Delete one property from view
      this.propertyListViewDynamic
        .getViewStore()
        .getEventEmitter()
        .addListener(PropertyListViewStoreEvent.unsaveProperty, this.onUnsavePropertyList);
    }

    // Unsave property from view
    if (this.listType === MyAccountListTypeEnum.saved) {
      this.propertySavedStoreService
        .getEventEmitter()
        .addListener(PropertySavedStoreEvent.unsave, this.onUnsavePropertySavedStoreService);
    }
  }

  /**
   * Render my account list
   */
  private renderPropertyList(): void {
    if (!this.htmlElement.propertyList) {
      return;
    }
    if (!this.isPageTypeSavedSearches) {
      this.propertyListViewDynamic.render();
    }
  }

  /**
   * Render email alert unsubscribe view
   */
  private renderEmailAlertUnsubscribe(): void {
    this.emailAlertUnsubscribeView.render();
  }

  /**
   * fetches user saved-searches
   */
  private fetchSaveSearches = (): void => {
    this.saveSearchStoreService.getAll(true);
  };

  /*
   * Sort property collection
   */
  private sortCollection(collection: PropertyModel[], option: string): PropertyModel[] {
    if (option === MyAccountSortOptionEnum.saveDate) {
      return this.sortPropertiesBySaveDate(collection);
    } else if (option === MyAccountSortOptionEnum.contactDate) {
      return this.sortPropertiesByContactDate(collection);
    } else if (option === MyAccountSortOptionEnum.modifiedDate) {
      return this.myAccountStore.sortPropertiesByNoteModifiedDate(collection);
    } else {
      return collection.sort((a: PropertyModel, b: PropertyModel) => {
        if (option === MyAccountSortOptionEnum.lowPrice) {
          return a.default_price - b.default_price;
        }

        if (option === MyAccountSortOptionEnum.highPrice) {
          return b.default_price - a.default_price;
        }

        if (option === MyAccountSortOptionEnum.recent) {
          return parseInt(b.id, 10) - parseInt(a.id, 10);
        }
        return;
      });
    }
  }

  /**
   * Sort properties by save date
   */
  private sortPropertiesBySaveDate(collection: PropertyModel[]): PropertyModel[] {
    const savedProperties: PropertySavedModel[] = this.propertySavedStoreService
      .getAllFromCache()
      .sort((a: PropertySavedModel, b: PropertySavedModel) => {
        return new Date(b.save_date).getTime() - new Date(a.save_date).getTime();
      });

    const properties: PropertyModel[] = savedProperties.map((savedModel: PropertySavedModel) => {
      return collection.find((collectionModel: PropertyModel) => {
        return parseInt(collectionModel.id, 10) === savedModel.property_id;
      });
    });

    return properties.filter((property) => {
      return property !== undefined;
    });
  }

  /**
   * Sort properties by contact date
   */
  private sortPropertiesByContactDate(collection: PropertyModel[]): PropertyModel[] {
    const contactedProperties: PropertyContactedModel[] = this.propertyContactedStoreService
      .getAllFromCache()
      .sort((a: PropertyContactedModel, b: PropertyContactedModel) => {
        return new Date(b.contact_date).getTime() - new Date(a.contact_date).getTime();
      });

    const properties: PropertyModel[] = contactedProperties.map((contactedModel: PropertyContactedModel) => {
      return collection.find((collectionModel: PropertyModel) => {
        return parseInt(collectionModel.id, 10) === contactedModel.property_id;
      });
    });

    return properties.filter((property) => {
      return property !== undefined;
    });
  }

  /**
   * Sync property saved store
   */
  private syncPropertySavedStore(collection: PropertyModel[], propertiesFromCache: PropertySavedModel[]): void {
    const savedProperties: PropertySavedModel[] = collection.map((propertyModel: PropertyModel) => {
      return propertiesFromCache.find((savedModel: PropertySavedModel) => {
        return parseInt(propertyModel.id, 10) === savedModel.property_id;
      });
    });
    this.propertySavedStoreService.sync(savedProperties);
    this.propertySavedStoreService.syncIdb(savedProperties);
  }

  /**
   * Sync property contacted store
   */
  private syncPropertyContactedStore(collection: PropertyModel[], propertiesFromCache: PropertyContactedModel[]): void {
    const contactedProperties: PropertyContactedModel[] = collection.map((propertyModel: PropertyModel) => {
      return propertiesFromCache.find((contactedModel: PropertyContactedModel) => {
        return parseInt(propertyModel.id, 10) === contactedModel.property_id;
      });
    });
    this.propertyContactedStoreService.sync(contactedProperties);
    this.propertyContactedStoreService.syncIdb(contactedProperties);
  }

  /**
   * Delete property from view
   */
  private deletePropertyFromView(propertyId: string): void {
    const properties = this.propertyListViewDynamic
      .getViewStore()
      .getState()
      .properties.map((property) => property.property)
      .filter((property) => property.id !== propertyId);

    this.myAccountStore.getState().properties.delete(propertyId);

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.deleteSuccess,
      payload: properties,
    });
  }

  /**
   * Sync property saved store
   */
  private onSyncPropertySavedStore = () => {
    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.fetchSavedProperties,
      payload: {},
    });
  };

  /**
   * Sync property clear
   */
  private onClearPropertyStore = () => {
    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.clearAll,
      payload: {},
    });
  };

  /**
   * Clear
   */
  private onClearAllAction = () => {
    this.myAccountStore.getState().properties.deleteAll();
    this.propertyListViewDynamic.getViewStore().updateProperties([], {});
  };

  /**
   * Sync property contacted store
   */
  private onSyncPropertyContactedStore = () => {
    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.fetchContactedProperties,
      payload: {},
    });
  };

  /**
   * Update list properties
   */
  private onUpdateListPropertiesAction = (properties: PropertyModel[]) => {
    this.propertyListViewDynamic.getViewStore().updateProperties(properties, {});
  };

  /**
   * Enable list
   */
  private onEnableListAction = () => {
    this.propertyListViewDynamic.getViewStore().updateDisabled(false);
  };

  /**
   * Disable list
   */
  private onDisableListAction = () => {
    this.propertyListViewDynamic.getViewStore().updateDisabled(true);
  };

  /**
   * On log out succeeded
   */
  private onLogOutSucceeded = (): void => {
    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.clearAll,
      payload: {},
    });
  };

  /**
   * Fetch saved properties
   */
  private onFetchSavedPropertiesAction = () => {
    const propertiesFromCache: PropertySavedModel[] = this.propertySavedStoreService.getAllFromCache();
    const ids = propertiesFromCache.map((model) => String(model.property_id));

    if (!ids.length) {
      return;
    }

    const searchPromise = this.myAccountStore.getState().properties.search({
      filter: {
        ids,
        allow_expired: true,
      },
      page: {
        limit: ids.length,
      },
    });

    this.fetchPropertiesCancelable = new PromiseCancelable(searchPromise);

    this.fetchPropertiesCancelable.getPromise().then(this.onFetchSavedPropertiesCancelable);
  };

  /**
   * After Fetching saved properties sort them and transit success event
   */
  private onFetchSavedPropertiesCancelable = (searchOutput: PropertyStoreSearchOutputInterface) => {
    if (!searchOutput) {
      return;
    }

    const propertiesFromCache: PropertySavedModel[] = this.propertySavedStoreService.getAllFromCache();

    this.syncPropertySavedStore(searchOutput.properties, propertiesFromCache);

    const sortPropertiesBySaveDate: PropertyModel[] = this.sortPropertiesBySaveDate(searchOutput.properties);

    // Add properties to property store global service
    this.propertyStoreService.initialize({
      models: sortPropertiesBySaveDate,
    });

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.fetchPropertiesSuccess,
      payload: {
        listType: MyAccountListTypeEnum.saved,
        properties: sortPropertiesBySaveDate,
      },
    });
  };

  /**
   * Fetch contacted properties
   */
  private onFetchContactedPropertiesAction = () => {
    const propertiesFromCache: PropertyContactedModel[] = this.propertyContactedStoreService.getAllFromCache();
    const ids = propertiesFromCache.map((model) => String(model.property_id));

    if (!ids.length) {
      return;
    }

    const searchPromise = this.myAccountStore.getState().properties.search({
      filter: {
        ids,
        allow_expired: true,
      },
      page: {
        limit: ids.length,
      },
    });

    this.fetchPropertiesCancelable = new PromiseCancelable(searchPromise);

    this.fetchPropertiesCancelable.getPromise().then(this.onFetchContactedPropertiesCancelable);
  };

  /**
   * After Fetching contacted properties sort them and transit success event
   */
  private onFetchContactedPropertiesCancelable = (searchOutput: PropertyStoreSearchOutputInterface) => {
    if (!searchOutput) {
      return;
    }

    const propertiesFromCache: PropertyContactedModel[] = this.propertyContactedStoreService.getAllFromCache();

    this.syncPropertyContactedStore(searchOutput.properties, propertiesFromCache);

    const sortPropertiesByContactDate: PropertyModel[] = this.sortPropertiesByContactDate(searchOutput.properties);

    // Add properties to property store global service
    this.propertyStoreService.initialize({
      models: sortPropertiesByContactDate,
    });

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.fetchPropertiesSuccess,
      payload: {
        listType: MyAccountListTypeEnum.contacted,
        properties: sortPropertiesByContactDate,
      },
    });
  };

  /**
   * Delete all saved properties from view
   */
  private onDeleteAllViewDataAction = () => {
    if (this.isPageTypeSavedSearches) {
      this.saveSearchStoreService.getEventEmitter().emit(SaveSearchStoreEvent.requestDeleteAll);
    } else {
      this.propertyListViewDynamic.getViewStore().updateProperties([], {});
      const savedProperties = this.myAccountStore.getState().properties.getAll();
      const tealiumAdapter = new StatsTealiumAdapter();
      const tealiumData = tealiumAdapter.toPropertyTealiumStats(savedProperties);
      const tealiumSendData: StatsTealiumDataLayerEventInterface = {
        sendToGa: true,
        tealium_event: StatsTealiumDataLayerTealiumEventEnum.savedPropertyRemoveAll,
        event_action: StatsTealiumDataLayerEventActionEnum.savedPropertyRemoveAll,
        event_category: StatsTealiumDataLayerEventCategoryEnum.savedProperties,
        event_label: '',
        ...tealiumData,
      };
      this.statsTealium.send(tealiumSendData);
    }
  };

  /**
   * Delete all properties from local storage
   */
  private onDeleteAllLocalStorageDataAction = () => {
    if (this.listType === MyAccountListTypeEnum.contacted) {
      this.propertyContactedStoreService.deleteAllFromLocalStorage();
    } else if (this.listType === MyAccountListTypeEnum.saved) {
      this.propertySavedStoreService.deleteAllFromLocalStorage();
    }
  };

  /**
   * Delete all saved properties from API
   */
  private onDeleteAllApiDataAction = () => {
    if (this.listType === MyAccountListTypeEnum.contacted) {
      this.propertyContactedStoreService.deleteAll();
    } else if (this.listType === MyAccountListTypeEnum.saved) {
      this.propertySavedStoreService.deleteAll();
    } else if (this.listType === MyAccountListTypeEnum.notes) {
      this.myAccountStore.deleteAllNotesFromApiAction();
    }
  };

  /**
   * Delete saved property state change event
   */
  private onDeletePropertyPropertyList = (propertyModel: PropertyModel): void => {
    if (this.listType === MyAccountListTypeEnum.saved) {
      this.propertySavedStoreService.unsave(propertyModel.id);
      this.propertySavedStoreService.delete(propertyModel.id);

      return;
    }

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.delete,
      payload: propertyModel.id,
    });
  };

  /**
   * Delete saved property state change event
   */
  private onUnsavePropertyList = (propertyModel: PropertyModel): void => {
    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.unsave,
      payload: propertyModel,
    });
  };

  /**
   *  Unsave property state change event
   */
  private onUnsavePropertySavedStoreService = (propertyId: string): void => {
    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.unsave,
      payload: propertyId,
    });
  };

  /**
   * Delete saved property from view
   */
  private onDeleteViewDataAction = (propertyId: string) => {
    this.deletePropertyFromView(propertyId);
  };

  /**
   * Delete one note from view
   */
  private onDeleteNoteViewData = (noteId: string) => {
    const propertyId = String(this.myNoteService.find(noteId).property_id);
    this.deletePropertyFromView(propertyId);
  };

  /**
   * Delete saved property from local storage
   */
  private onDeleteLocalStorageDataAction = (propertyId: string) => {
    if (this.listType === MyAccountListTypeEnum.contacted) {
      this.propertyContactedStoreService.deleteFromLocalStorage(propertyId);
    }
  };

  /**
   * Delete saved property from API
   */
  private onDeleteApiDataAction = (propertyId: string) => {
    this.propertyContactedStoreService.delete(propertyId);
  };

  /**
   * Filter property collection
   */
  private filterCollection(collection: PropertyModel[], categoryId: string = ''): PropertyModel[] {
    let filteredProps;

    if (categoryId !== '') {
      filteredProps = collection.filter((propertyModel) => propertyModel.category_id === categoryId, 10);
    } else {
      filteredProps = collection;
    }

    return filteredProps;
  }

  /**
   * Cancel previous fetch
   */
  private onCancelPreviousFetchAction = () => {
    if (!this.fetchPropertiesCancelable) {
      return;
    }

    this.fetchPropertiesCancelable.cancel();
  };

  /**
   * Show confirmation popup
   */
  private onShowConfirmationPopup = async () => {
    let renderPopup = false;
    const { length } = this.myAccountStore.getState().properties.getAll();

    if (this.isPageTypeSavedSearches) {
      const savedSearches = await this.saveSearchStoreService.getAll();
      renderPopup = savedSearches?.length > 0;
    } else if (length) {
      renderPopup = true;
    }

    if (renderPopup) {
      this.myAccountPopupView.render();

      this.myAccountPopupView.open();
    }
  };

  /**
   * Hide confirmation popup
   */
  private onHideConfirmationPopup = (): void => {
    this.myAccountPopupView.close();
  };

  /**
   * Filter action
   */
  private onFilterAction = (filterOptions: MyAccountFilterOptionsInterface): void => {
    const collection = this.myAccountStore.getState().properties.getAll();
    const filteredProperties = this.filterCollection(collection, filterOptions.categoryId);
    const sortedPropsList = this.sortCollection(filteredProperties, filterOptions.sort);
    const listType =
      this.listType === MyAccountListTypeEnum.contacted
        ? 'contacted_properties'
        : this.listType === MyAccountListTypeEnum.notes
        ? 'my_notes_properties'
        : 'saved_properties';

    // Log stats
    this.statsService.ctaClick({
      [listType]: true,
      cta: {
        filter: {
          property: <CtaFilterPropertyType>filterOptions.categoryId || '0',
        },
      },
    });

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.filterSuccess,
      payload: sortedPropsList,
    });
  };

  /**
   * Sort action
   */
  private onSortAction = (filterOptions: MyAccountFilterOptionsInterface): void => {
    const collection = this.myAccountStore.getState().properties.getAll();
    const filteredProperties = this.filterCollection(collection, filterOptions.categoryId);
    const sortedPropsList = this.sortCollection(filteredProperties, filterOptions.sort);

    if (this.listType === MyAccountListTypeEnum.contacted) {
      this.statsService.ctaClick({
        cta: {
          sort: {
            contactedProperty: <CtaSortContactedPropertyType>filterOptions.sort,
          },
        },
      });
    } else if (this.listType === MyAccountListTypeEnum.notes) {
      this.statsService.ctaClick({
        cta: {
          sort: {
            myNotesProperty: <CtaSortMyNotesPropertyType>filterOptions.sort,
          },
        },
      });
    } else {
      this.statsService.ctaClick({
        cta: {
          sort: {
            savedProperty: <CtaSortSavedPropertyType>filterOptions.sort,
          },
        },
      });
    }

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.sortSuccess,
      payload: sortedPropsList,
    });
  };

  /**
   * Check if list type is same then refresh property list
   */
  private onCheckIfRefreshListIsNeeded = (payload: { listType: string; properties: PropertyModel[] }) => {
    if (payload.listType === this.listType) {
      this.myAccountStatechartStore.transit({
        event: MyAccountStatechartEvent.refreshList,
        payload: payload.properties,
      });
    }
  };
}
